import { forwardRef, useState, useEffect, useCallback } from 'react';
import type { DeviceWidth } from './BuilderToolbar.tsx';

interface Props {
  src: string;
  deviceWidth: DeviceWidth;
  isReady: boolean;
}

const DEVICE_WIDTHS: Record<string, number> = {
  '375': 375,
  '768': 768,
};

/**
 * The iframe that embeds the website app in edit mode.
 * Resizes the canvas based on the selected device width.
 * Listens for CONTENT_HEIGHT messages from the iframe to auto-resize.
 */
export const BuilderIframe = forwardRef<HTMLIFrameElement, Props>(
  ({ src, deviceWidth, isReady }, ref) => {
    const canvasWidth = deviceWidth != null ? DEVICE_WIDTHS[String(deviceWidth)] : null;
    const [contentHeight, setContentHeight] = useState<number | null>(null);

    // Listen for height reports from the iframe
    const handleMessage = useCallback((event: MessageEvent) => {
      const msg = event.data as { type?: string; height?: number };
      if (msg?.type === 'CONTENT_HEIGHT' && typeof msg.height === 'number') {
        setContentHeight(msg.height);
      }
    }, []);

    useEffect(() => {
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }, [handleMessage]);

    // Also poll iframe height as fallback (ResizeObserver inside iframe is more reliable,
    // but this catches cases where the website doesn't send CONTENT_HEIGHT)
    useEffect(() => {
      if (!isReady) return;
      const iframeEl = typeof ref === 'function' ? null : ref?.current;
      if (!iframeEl) return;

      const interval = setInterval(() => {
        try {
          const h = iframeEl.contentDocument?.documentElement?.scrollHeight;
          if (h && h > 100) setContentHeight(h);
        } catch {
          // Cross-origin — can't read, rely on postMessage
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [isReady, ref]);

    const iframeHeight = contentHeight ? `${contentHeight}px` : '100vh';

    return (
      <div className="relative flex flex-1 items-start justify-center overflow-auto bg-gray-100 p-4">
        {/* Loading overlay */}
        {!isReady && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100/80">
            <svg className="h-6 w-6 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
            </svg>
          </div>
        )}

        <div
          className="overflow-hidden rounded-sm shadow-2xl transition-all duration-300"
          style={{
            width: canvasWidth != null ? canvasWidth : '100%',
            minHeight: iframeHeight,
          }}
        >
          <iframe
            ref={ref}
            src={src}
            className="w-full border-0 bg-white"
            title="Visual Builder preview"
            style={{ height: iframeHeight }}
          />
        </div>
      </div>
    );
  },
);

BuilderIframe.displayName = 'BuilderIframe';
